import { useEffect, useState } from 'react';
import Icon from '../common/Icon.jsx';

/**
 * StatusBar — fake-but-faithful imitation of the device status bar.
 *
 * Why this exists: when the app is viewed in a desktop browser the OS chrome
 * isn't there, and the design intent (a deep-black bezel with a thin clock
 * strip) gets lost. On a real device this is hidden by the actual status
 * bar via the `viewport-fit=cover` meta tag and safe-area insets.
 *
 * It is purely cosmetic — never read state out of it.
 */
export default function StatusBar() {
  const [now, setNow] = useState(() => formatTime(new Date()));

  useEffect(() => {
    // Tick on the next minute, then every 60s. Avoids drift from a flat 60000ms interval.
    let timer;
    const align = () => {
      const wait = 60_000 - (Date.now() % 60_000);
      timer = setTimeout(() => {
        setNow(formatTime(new Date()));
        align();
      }, wait);
    };
    align();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="relative z-40 flex items-center justify-between px-6 pt-2 text-2xs font-semibold text-ink-primary"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
    >
      <span className="font-mono tabular-nums tracking-tight">{now}</span>
      <div className="flex items-center gap-1.5 text-ink-primary">
        <Icon name="signal" size={14} strokeWidth={0} className="text-ink-primary" />
        <Icon name="wifi" size={14} />
        <Icon name="battery" size={18} />
      </div>
    </div>
  );
}

function formatTime(d) {
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  // 24-hour clock — matches the FOM brand guideline (race timing is always 24h).
  return `${String(h).padStart(2, '0')}:${m}`;
}
